
<div class="mb-3 flex items-center gap-2">
    <app-breadcrumbs [items]="breadcrumbItems()" />

    @if (mode() === 'detail' && caseItem()?.caseUniqueId) {
      <button
        type="button"
        (click)="copyCaseUniqueId()"
        class="inline-flex items-center justify-center rounded-md border border-transparent bg-transparent p-1 text-zinc-600 transition hover:border-zinc-300 hover:text-zinc-800 focus-visible:border-zinc-300 focus-visible:outline-none"
        aria-label="Copy case ID"
        title="Copy case ID"
      >
        <svg class="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
          <path
            d="M9 9.75A2.25 2.25 0 0 1 11.25 7.5h6A2.25 2.25 0 0 1 19.5 9.75v6A2.25 2.25 0 0 1 17.25 18h-6A2.25 2.25 0 0 1 9 15.75v-6Z"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
          <path
            d="M15 7.5V6.75A2.25 2.25 0 0 0 12.75 4.5h-6A2.25 2.25 0 0 0 4.5 6.75v6A2.25 2.25 0 0 0 6.75 15H9"
            stroke="currentColor"
            stroke-width="1.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    }
</div>

<section class="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
  <div class="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 pb-4">
    <div>
      <h1 class="text-xl font-semibold text-slate-900">
        @if (mode() === 'create') {
          Create Case
        } @else {
          Case Details
        }
      </h1>
      <p class="mt-1 text-sm text-slate-500">
        {{ clientDisplayName() }}
      </p>

      @if (mode() === 'detail' && caseItem(); as activeCase) {
        <div class="mt-2 flex flex-col items-start gap-2">
          <div
            class="priority-badge inline-flex items-center rounded-full border px-2.5 py-1 text-xs uppercase tracking-wide"
            [class.priority-badge-high]="activeCase.priority.toUpperCase() === 'HIGH'"
          >
            Priority: {{ activeCase.priority }}
          </div>
        </div>
      }
    </div>

      @if (mode() === 'detail' && caseItem(); as activeCase) {
        <div class="flex items-center gap-2">
          <span class="text-xs font-semibold uppercase tracking-wide text-zinc-500">Status</span>

          <div
            class="status-segmented"
            [class.is-open]="activeCase.status === 'open'"
            [class.is-closed]="activeCase.status === 'closed'"
            [class.is-declined]="activeCase.status === 'declined_closed'"
            [class.is-updating]="togglingStatus()"
          >
            <span class="status-segmented__thumb" aria-hidden="true"></span>

            @for (statusOption of caseStatusOptions; track statusOption.value) {
              <button
                type="button"
                (click)="requestCaseStatusChange(statusOption.value)"
                [disabled]="togglingStatus() || activeCase.status === statusOption.value"
                [class.is-active]="activeCase.status === statusOption.value"
                class="status-segmented__button"
              >
                {{ statusOption.label }}
              </button>
            }
          </div>
        </div>
      }
  </div>

  @if (pageLoading()) {
    <div class="mt-5 rounded-2xl border border-slate-200 bg-slate-50 p-6 text-sm text-slate-600">
      Loading case data...
    </div>
  } @else {
    @if (mode() === 'create') {
      <form [formGroup]="caseForm" (ngSubmit)="submitCreateCase()" class="mt-5 space-y-5">
        <div class="grid gap-4 md:grid-cols-2">
          <div>
            <label for="service" class="mb-1 block text-sm font-medium text-slate-700">Service</label>
            <select
              id="service"
              formControlName="serviceId"
              class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
            >
              <option [value]="0">Select a service</option>
              @for (service of services(); track service.id) {
                <option [value]="service.id">{{ service.name }}</option>
              }
            </select>
            @if (caseForm.controls.serviceId.touched && caseForm.controls.serviceId.invalid) {
              <p class="mt-1 text-xs text-red-600">Service is required.</p>
            }
          </div>

          <div>
            <label for="partner" class="mb-1 block text-sm font-medium text-slate-700">Partner</label>
            <select
              id="partner"
              formControlName="partnerOrganizationId"
              class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
            >
              <option [value]="0">Select a partner</option>
              @for (partner of partners(); track partner.id) {
                <option [value]="partner.id">{{ partner.name }}</option>
              }
            </select>
            @if (caseForm.controls.partnerOrganizationId.touched && caseForm.controls.partnerOrganizationId.invalid) {
              <p class="mt-1 text-xs text-red-600">Partner is required.</p>
            }
          </div>

          <div>
            <label for="referralSource" class="mb-1 block text-sm font-medium text-slate-700">Referral Source</label>
            <select
              id="referralSource"
              formControlName="referralSource"
              class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
            >
              <option value="">Select referral source</option>
              @for (option of referralSourceOptions; track option.value) {
                <option [value]="option.value">{{ option.label }}</option>
              }
            </select>
            @if (caseForm.controls.referralSource.touched && caseForm.controls.referralSource.invalid) {
              <p class="mt-1 text-xs text-red-600">Referral source is required.</p>
            }
          </div>

          <div>
            <label for="referralDate" class="mb-1 block text-sm font-medium text-slate-700">Referral Date</label>
            <input
              id="referralDate"
              type="date"
              formControlName="referralDate"
              class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
            />
            @if (caseForm.controls.referralDate.touched && caseForm.controls.referralDate.invalid) {
              <p class="mt-1 text-xs text-red-600">Referral date is required.</p>
            }
          </div>
        </div>

        @if (caseForm.controls.referralSource.value === 'other') {
          <div>
            <label for="referralSourceOther" class="mb-1 block text-sm font-medium text-slate-700">Referral Source (Other)</label>
            <input
              id="referralSourceOther"
              type="text"
              formControlName="referralSourceOther"
              class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
              placeholder="Enter referral source details"
            />
          </div>
        }

        <div>
          <label for="priority" class="mb-1 block text-sm font-medium text-slate-700">Priority</label>
          <select
            id="priority"
            formControlName="priority"
            class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200 md:w-64"
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
          </select>
        </div>

        <div class="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-800">
          Partner dropdown is filtered to organizations approved for the selected service.
        </div>

        <div class="flex flex-wrap justify-end gap-2">
          <button
            type="button"
            (click)="goBackToClient()"
            class="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            [disabled]="savingCase()"
            class="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {{ savingCase() ? 'Creating...' : 'Create Case' }}
          </button>
        </div>
      </form>
    } @else if (caseItem(); as activeCase) {
      <div class="mt-5 space-y-4">
        <div class="rounded-2xl border border-slate-200 bg-white p-4">
          <h3 class="text-sm font-semibold text-slate-900">Assignment</h3>
          <dl class="mt-3 grid gap-2 text-sm sm:grid-cols-2">
            <div class="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
              <dt class="text-slate-500">Service</dt>
              <dd class="mt-1 font-medium text-slate-900">{{ serviceName() }}</dd>
            </div>
            <div class="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
              <dt class="text-slate-500">Partner</dt>
              <dd class="mt-1 font-medium text-slate-900">{{ partnerName() }}</dd>
            </div>
            <div class="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
              <dt class="text-slate-500">Referral Source</dt>
              <dd class="mt-1 font-medium text-slate-900">{{ referralSourceLabel() }}</dd>
            </div>
            <div class="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
              <dt class="text-slate-500">Referral Date</dt>
              <dd class="mt-1 font-medium text-slate-900">{{ activeCase.referralDate | date:'MMM d, y':'UTC' }}</dd>
            </div>
          </dl>
        </div>

        <div class="grid gap-4 lg:grid-cols-2">
          <div class="space-y-4">
            <div class="rounded-2xl border border-slate-200 bg-white p-4">
              <h3 class="text-sm font-semibold text-slate-900">Latest Case Note</h3>
              @if (latestCaseNote(); as latestNote) {
                <p class="mt-2 text-xs text-slate-500">
                  {{ latestNote.authorName }} · {{ formatDateTime(latestNote.createdAt) }}
                </p>
                <p class="mt-2 whitespace-pre-line text-sm text-slate-700">{{ latestNote.note }}</p>

                <div class="mt-3 grid gap-2 text-xs text-slate-600 sm:grid-cols-2">
                  <div>
                    <span class="font-semibold text-slate-700">Note Date:</span>
                    {{ formatNoteDate(latestNote.noteDate) }}
                  </div>
                  @if (latestNote.startTime && latestNote.endTime) {
                    <div>
                      <span class="font-semibold text-slate-700">Time:</span>
                      {{ latestNote.startTime }} - {{ latestNote.endTime }}
                      ({{ ((latestNote.durationMinutes || 0) / 60).toFixed(2) }} hrs)
                    </div>
                  }
                  @if (latestNote.serviceUnits !== null) {
                    <div>
                      <span class="font-semibold text-slate-700">{{detailServiceUnitLabel()}}:</span>
                      {{ latestNote.serviceUnits }}
                    </div>
                  }
                </div>
              } @else {
                <p class="mt-2 text-sm text-slate-600">No notes yet for this case.</p>
              }
            </div>

            <form [formGroup]="noteForm" (ngSubmit)="submitNote()" class="space-y-3 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <h3 class="text-sm font-semibold text-slate-900">Add New Case Note</h3>

              <label class="block text-xs font-medium text-slate-700">Case Note <span class="text-red-600">*</span></label>
              <textarea
                formControlName="note"
                rows="4"
                placeholder="Enter case note details"
                class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none placeholder:text-slate-400 focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
              ></textarea>

              <div class="grid gap-3 sm:grid-cols-2">
                <div>
                  <label class="mb-1 block text-xs font-medium text-slate-700">Note Date <span class="text-red-600">*</span></label>
                  <input
                    type="date"
                    formControlName="noteDate"
                    class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                  />
                </div>
                <div>
                  <label class="mb-1 block text-xs font-medium text-slate-700">{{ detailServiceUnitLabel() }}</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    formControlName="serviceUnits"
                    class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                  />
                </div>
                <div>
                  <label class="mb-1 block text-xs font-medium text-slate-700">Start Time</label>
                  <input
                    type="time"
                    formControlName="startTime"
                    class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                  />
                </div>
                <div>
                  <label class="mb-1 block text-xs font-medium text-slate-700">End Time</label>
                  <input
                    type="time"
                    formControlName="endTime"
                    class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                  />
                </div>

                <div>
                  <label class="mb-1 block text-xs font-medium text-slate-700">Type of Contact <span class="text-red-600">*</span></label>
                  <select
                    formControlName="contactType"
                    class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                  >
                    @for (option of noteContactTypeOptions; track option.value) {
                      <option [value]="option.value">{{ option.label }}</option>
                    }
                  </select>
                </div>

                <div>
                  <label class="mb-1 block text-xs font-medium text-slate-700">Location Type <span class="text-red-600">*</span></label>
                  <select
                    formControlName="locationType"
                    class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                  >
                    @for (option of noteLocationTypeOptions; track option.value) {
                      <option [value]="option.value">{{ option.label }}</option>
                    }
                  </select>
                </div>

                <div>
                  <label class="mb-1 block text-xs font-medium text-slate-700">Follow-Up Required</label>
                  <select
                    formControlName="followUpRequired"
                    class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                  >
                    <option [ngValue]="false">No</option>
                    <option [ngValue]="true">Yes</option>
                  </select>
                </div>
              </div>

              @if (noteDurationHours() !== null) {
                <div class="text-xs text-slate-600">Computed duration: {{ noteDurationHours() }} hours</div>
              }

              @if (noteSubmitAttempted() && noteForm.invalid) {
                <div class="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Please complete all required fields.
                </div>
              }

              <div class="flex justify-end">
                <button
                  type="submit"
                  [disabled]="savingNote()"
                  class="rounded-xl bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {{ savingNote() ? 'Saving Note...' : 'Add Note' }}
                </button>
              </div>
            </form>
          </div>

          <div class="rounded-2xl border border-slate-200 bg-white p-4">
            <h2 class="text-base font-semibold text-slate-900">Timeline</h2>
            @if (loadingNotes()) {
              <div class="mt-4 rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-600">
                Loading notes...
              </div>
            } @else {
              <ol class="timeline-list mt-4 space-y-4">
                @for (event of timelineEvents(); track $index) {
                  <li class="timeline-item pl-6">
                    <div class="timeline-content rounded-xl border border-slate-200 bg-slate-50 p-3">
                      @if (event.type === 'created') {
                        <p class="text-sm font-semibold text-slate-900">Case Created</p>
                        <p class="text-xs text-slate-500">{{ formatDateTime(activeCase.openedAt) }}</p>

                        <div class="mt-2 grid gap-2 text-xs text-slate-600 sm:grid-cols-2">
                          <div>
                            <span class="font-semibold text-slate-700">Created by:</span>
                            {{ getCaseCreatorLabel(activeCase) }}
                          </div>
                          <div>
                            <span class="font-semibold text-slate-700">Assigned to:</span>
                            {{ getCaseAssignedPartnerLabel(activeCase) }}
                          </div>
                        </div>
                      } @else if (event.type === 'status') {
                        <p class="text-sm font-semibold text-slate-900">
                          @if ($any(event.payload).toStatus === 'open') {
                            Case Reopened
                          } @else if ($any(event.payload).toStatus === 'declined_closed') {
                            Case Declined/Closed
                          } @else {
                            Case Closed
                          }
                        </p>
                        <p class="text-xs text-slate-500">
                          {{ $any(event.payload).actorName }} · {{ formatDateTime($any(event.payload).createdAt) }}
                        </p>

                        @if ($any(event.payload).summary?.trim()) {
                          <p class="mt-2 whitespace-pre-line text-sm text-slate-700">{{ $any(event.payload).summary }}</p>
                        }
                      } @else {
                        <p class="text-sm font-semibold text-slate-900">New Case Note</p>
                        <p class="text-xs text-slate-500">
                          {{ $any(event.payload).authorName }} · {{ formatDateTime($any(event.payload).createdAt) }}
                        </p>
                        <p class="mt-2 whitespace-pre-line text-sm text-slate-700">{{ $any(event.payload).note }}</p>

                        <div class="mt-2 grid gap-2 text-xs text-slate-600 sm:grid-cols-2">
                          <div>
                            <span class="font-semibold text-slate-700">Note Date:</span>
                            {{ formatNoteDate($any(event.payload).noteDate) }}
                          </div>
                          @if ($any(event.payload).serviceUnits !== null) {
                            <div>
                              <span class="font-semibold text-slate-700">{{ detailServiceUnitLabel() }}:</span>
                              {{ $any(event.payload).serviceUnits }}
                            </div>
                          }
                          @if ($any(event.payload).startTime && $any(event.payload).endTime) {
                            <div>
                              <span class="font-semibold text-slate-700">Time:</span>
                              {{ $any(event.payload).startTime }} - {{ $any(event.payload).endTime }}
                              ({{ (($any(event.payload).durationMinutes || 0) / 60).toFixed(2) }} hrs)
                            </div>
                          }
                          <div>
                            <span class="font-semibold text-slate-700">Contact:</span>
                            {{ formatContactType($any(event.payload).contactType) }}
                          </div>
                          <div>
                            <span class="font-semibold text-slate-700">Location:</span>
                            {{ formatLocationType($any(event.payload).locationType) }}
                          </div>
                          <div>
                            <span class="font-semibold text-slate-700">Follow-Up Required:</span>
                            {{ $any(event.payload).followUpRequired ? 'Yes' : 'No' }}
                          </div>
                        </div>
                      }
                    </div>
                  </li>
                }
              </ol>
            }
          </div>
        </div>
      </div>
    }
  }
</section>

@if (pendingStatusChange(); as nextStatus) {
  <div class="fixed inset-0 z-40 bg-slate-900/50" (click)="cancelStatusChange()"></div>

  <div class="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="case-status-modal-title">
    <article class="w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-5 shadow-xl sm:p-6">
      <h3 id="case-status-modal-title" class="text-lg font-semibold text-slate-900">
        {{ nextStatus === 'declined_closed' ? 'Decline and Close Case' : 'Close Case' }}
      </h3>

      <p class="mt-2 text-sm text-slate-600">
        Add a summary that will be saved in the timeline and included in closure notifications.
      </p>

      <div class="mt-4">
        <label for="closureSummary" class="mb-1 block text-sm font-medium text-slate-700">Summary</label>
        <textarea
          id="closureSummary"
          [value]="pendingStatusSummary()"
          (input)="pendingStatusSummary.set($any($event.target).value)"
          rows="4"
          placeholder="Enter closure summary"
          class="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
        ></textarea>
      </div>

      <div class="mt-5 flex justify-end gap-2">
        <button
          type="button"
          class="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          (click)="cancelStatusChange()"
          [disabled]="togglingStatus()"
        >
          Cancel
        </button>

        <button
          type="button"
          class="rounded-xl bg-rose-700 px-4 py-2 text-sm font-medium text-white hover:bg-rose-600 disabled:cursor-not-allowed disabled:opacity-70"
          (click)="confirmStatusChange()"
          [disabled]="togglingStatus()"
        >
          {{ nextStatus === 'declined_closed' ? 'Confirm Decline/Close' : 'Confirm Close' }}
        </button>
      </div>
    </article>
  </div>
}
